// Brand Configuration
// ===================
// Change this to switch between brands: 'default', 'pentatonic', 'kiehls', 'example'
export const SELECTED_BRAND: string = '';

// Brand Configuration Types and Validation
export interface BrandColors {
  primary: {
    light: string;
    dark: string;
  };
  secondary: {
    light: string;
    dark: string;
  };
  tertiary: {
    light: string;
    dark: string;
  };
  background: {
    light: string;
    dark: string;
  };
  surface: {
    light: string;
    dark: string;
  };
  text: {
    primary: {
      light: string;
      dark: string;
    };
    secondary: {
      light: string;
      dark: string;
    };
    muted: {
      light: string;
      dark: string;
    };
  };
  border: {
    light: string;
    dark: string;
  };
  accent: {
    light: string;
    dark: string;
  };
}

export interface BrandTypography {
  primary: string;
  secondary: string;
  body: string;
  button: string;
  weights: {
    light: number;
    normal: number;
    medium: number;
    semibold: number;
    bold: number;
  };
  sizes: {
    heading: {
      title: string;
      subtitle: string;
    };
  };
  headingWeights: {
    title: {
      bold: number;
      regular: number;
    };
  };
  buttonStyle: {
    textTransform: string;
    letterSpacing: string;
    color: string;
    secondaryColor: string;
  };
}

export interface BrandLogos {
  main: {
    light: string;
    dark: string;
  };
}

export interface BrandBorderRadius {
  button: string;
}

export interface BrandVideo {
  background: {
    src: string;
    type: string;
    autoplay: boolean;
    muted: boolean;
    loop: boolean;
    playsInline: boolean;
  };
}

export interface BrandConfig {
  name: string;
  colors: BrandColors;
  typography: BrandTypography;
  logos: BrandLogos;
  borderRadius: BrandBorderRadius;
  video: BrandVideo;
}

// Default brand configuration (Pentatonic - from brand-config.json)
export const defaultBrandConfig: BrandConfig = {
  name: "Pentatonic",
  colors: {
    primary: {
      light: "#00FBA9",
      dark: "#00FBA9",
    },
    secondary: {
      light: "#171717",
      dark: "#00FBA9",
    },
    tertiary: {
      light: "#3941EB",
      dark: "#3941EB",
    },
    background: {
      light: "#ffffff",
      dark: "#171717",
    },
    surface: {
      light: "#fafafa",
      dark: "#000000",
    },
    text: {
      primary: {
        light: "#171717",
        dark: "#ffffff",
      },
      secondary: {
        light: "#A9A9A9",
        dark: "#fafafa",
      },
      muted: {
        light: "#A9A9A9",
        dark: "#A9A9A9",
      },
    },
    border: {
      light: "#A9A9A9",
      dark: "#A9A9A9",
    },
    accent: {
      light: "#ff4C00",
      dark: "#FFB788",
    },
  },
  typography: {
    primary: "Poppins",
    secondary: "Atkinson Hyperlegible Next",
    body: "Atkinson Hyperlegible Next",
    button: "Atkinson Hyperlegible Mono",
    weights: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    sizes: {
      heading: {
        title: "88px",
        subtitle: "45px",
      },
    },
    headingWeights: {
      title: {
        bold: 100,
        regular: 100,
      },
    },
    buttonStyle: {
      textTransform: "uppercase",
      letterSpacing: "0",
      color: "#171717",
      secondaryColor: "#171717",
    },
  },
  logos: {
    main: {
      light: "/assets/images/logos/pentatonic.png",
      dark: "/assets/images/logos/pentatonic-wob.png",
    },
  },
  borderRadius: {
    button: "100px",
  },
  video: {
    background: {
      src: "/assets/videos/attract-pentatonic.mp4",
      type: "video/mp4",
      autoplay: true,
      muted: true,
      loop: true,
      playsInline: true,
    },
  },
};

// Brand configuration validation
export function validateBrandConfig(config: any): BrandConfig {
  const errors: string[] = [];

  // Validate required fields
  if (!config.name || typeof config.name !== 'string') {
    errors.push('Brand name is required and must be a string');
  }

  if (!config.colors) {
    errors.push('Colors configuration is required');
  } else {
    const requiredColorKeys = ['primary', 'secondary', 'tertiary', 'background', 'surface', 'text', 'border', 'accent'];
    for (const key of requiredColorKeys) {
      if (!config.colors[key]) {
        errors.push(`Color configuration missing: ${key}`);
      } else if (['primary', 'secondary', 'tertiary'].includes(key)) {
        // Check light/dark variants for primary, secondary, tertiary
        if (!config.colors[key].light || !config.colors[key].dark) {
          errors.push(`Color configuration for ${key} must have both light and dark variants`);
        }
      }
    }

    // Validate color structure
    if (config.colors.background && (!config.colors.background.light || !config.colors.background.dark)) {
      errors.push('Background colors must have both light and dark variants');
    }
    if (config.colors.surface && (!config.colors.surface.light || !config.colors.surface.dark)) {
      errors.push('Surface colors must have both light and dark variants');
    }
    if (config.colors.text) {
      const textKeys = ['primary', 'secondary', 'muted'];
      for (const key of textKeys) {
        if (!config.colors.text[key] || !config.colors.text[key].light || !config.colors.text[key].dark) {
          errors.push(`Text color ${key} must have both light and dark variants`);
        }
      }
    }
  }

  if (!config.typography) {
    errors.push('Typography configuration is required');
  } else {
    if (!config.typography.primary || typeof config.typography.primary !== 'string') {
      errors.push('Primary font family is required');
    }
    if (!config.typography.secondary || typeof config.typography.secondary !== 'string') {
      errors.push('Secondary font family is required');
    }
    if (!config.typography.body || typeof config.typography.body !== 'string') {
      errors.push('Body font family is required');
    }
    if (!config.typography.button || typeof config.typography.button !== 'string') {
      errors.push('Button font family is required');
    }
    if (!config.typography.weights) {
      errors.push('Font weights configuration is required');
    }
    if (!config.typography.sizes) {
      errors.push('Font sizes configuration is required');
    } else {
      if (!config.typography.sizes.heading) {
        errors.push('Heading font sizes configuration is required');
      } else {
        if (!config.typography.sizes.heading.title || typeof config.typography.sizes.heading.title !== 'string') {
          errors.push('Title font size is required');
        }
        if (!config.typography.sizes.heading.subtitle || typeof config.typography.sizes.heading.subtitle !== 'string') {
          errors.push('Subtitle font size is required');
        }
      }
    }
    if (!config.typography.headingWeights) {
      errors.push('Heading weights configuration is required');
    } else {
      if (!config.typography.headingWeights.title) {
        errors.push('Title heading weights configuration is required');
      } else {
        if (typeof config.typography.headingWeights.title.bold !== 'number') {
          errors.push('Title bold weight must be a number');
        }
        if (typeof config.typography.headingWeights.title.regular !== 'number') {
          errors.push('Title regular weight must be a number');
        }
      }
    }
    if (!config.typography.buttonStyle) {
      errors.push('Button style configuration is required');
    } else {
      if (!config.typography.buttonStyle.textTransform || typeof config.typography.buttonStyle.textTransform !== 'string') {
        errors.push('Button text transform is required and must be a string');
      }
      if (!config.typography.buttonStyle.letterSpacing || typeof config.typography.buttonStyle.letterSpacing !== 'string') {
        errors.push('Button letter spacing is required and must be a string');
      }
      if (!config.typography.buttonStyle.color || typeof config.typography.buttonStyle.color !== 'string') {
        errors.push('Button color is required and must be a string');
      }
      if (!config.typography.buttonStyle.secondaryColor || typeof config.typography.buttonStyle.secondaryColor !== 'string') {
        errors.push('Button secondary color is required and must be a string');
      }
    }
  }

  if (!config.logos) {
    errors.push('Logos configuration is required');
  } else {
    if (!config.logos.main) {
      errors.push('Main logo configuration is required');
    } else {
      if (!config.logos.main.light || typeof config.logos.main.light !== 'string') {
        errors.push('Main logo light mode path is required');
      }
      if (!config.logos.main.dark || typeof config.logos.main.dark !== 'string') {
        errors.push('Main logo dark mode path is required');
      }
    }
  }

  if (!config.borderRadius) {
    errors.push('Border radius configuration is required');
  } else {
    if (!config.borderRadius.button || typeof config.borderRadius.button !== 'string') {
      errors.push('Button border radius is required');
    }
  }

  if (!config.video) {
    errors.push('Video configuration is required');
  } else {
    if (!config.video.background) {
      errors.push('Background video configuration is required');
    } else {
      if (!config.video.background.src || typeof config.video.background.src !== 'string') {
        errors.push('Background video source is required');
      }
      if (!config.video.background.type || typeof config.video.background.type !== 'string') {
        errors.push('Background video type is required');
      }
      if (typeof config.video.background.autoplay !== 'boolean') {
        errors.push('Background video autoplay must be a boolean');
      }
      if (typeof config.video.background.muted !== 'boolean') {
        errors.push('Background video muted must be a boolean');
      }
      if (typeof config.video.background.loop !== 'boolean') {
        errors.push('Background video loop must be a boolean');
      }
      if (typeof config.video.background.playsInline !== 'boolean') {
        errors.push('Background video playsInline must be a boolean');
      }
    }
  }

  if (errors.length > 0) {
    console.error('Brand configuration validation errors:', errors);
    throw new Error(`Invalid brand configuration: ${errors.join(', ')}`);
  }

  // Merge with defaults for missing optional fields
  return {
    ...defaultBrandConfig,
    ...config,
    colors: {
      ...defaultBrandConfig.colors,
      ...config.colors,
      primary: {
        ...defaultBrandConfig.colors.primary,
        ...config.colors.primary,
      },
      secondary: {
        ...defaultBrandConfig.colors.secondary,
        ...config.colors.secondary,
      },
      tertiary: {
        ...defaultBrandConfig.colors.tertiary,
        ...config.colors.tertiary,
      },
      background: {
        ...defaultBrandConfig.colors.background,
        ...config.colors.background,
      },
      surface: {
        ...defaultBrandConfig.colors.surface,
        ...config.colors.surface,
      },
      text: {
        ...defaultBrandConfig.colors.text,
        ...config.colors.text,
        primary: {
          ...defaultBrandConfig.colors.text.primary,
          ...config.colors.text.primary,
        },
        secondary: {
          ...defaultBrandConfig.colors.text.secondary,
          ...config.colors.text.secondary,
        },
        muted: {
          ...defaultBrandConfig.colors.text.muted,
          ...config.colors.text.muted,
        },
      },
      border: {
        ...defaultBrandConfig.colors.border,
        ...config.colors.border,
      },
      accent: {
        ...defaultBrandConfig.colors.accent,
        ...config.colors.accent,
      },
    },
    typography: {
      ...defaultBrandConfig.typography,
      ...config.typography,
      weights: {
        ...defaultBrandConfig.typography.weights,
        ...config.typography.weights,
      },
      sizes: {
        ...defaultBrandConfig.typography.sizes,
        ...config.typography.sizes,
        heading: {
          ...defaultBrandConfig.typography.sizes.heading,
          ...config.typography.sizes.heading,
        },
      },
      headingWeights: {
        ...defaultBrandConfig.typography.headingWeights,
        ...config.typography.headingWeights,
        title: {
          ...defaultBrandConfig.typography.headingWeights.title,
          ...config.typography.headingWeights.title,
        },
      },
      buttonStyle: {
        ...defaultBrandConfig.typography.buttonStyle,
        ...config.typography.buttonStyle,
      },
    },
    logos: {
      ...defaultBrandConfig.logos,
      ...config.logos,
      main: {
        ...defaultBrandConfig.logos.main,
        ...config.logos.main,
      },
    },
    borderRadius: {
      ...defaultBrandConfig.borderRadius,
      ...config.borderRadius,
    },
    video: {
      ...defaultBrandConfig.video,
      ...config.video,
      background: {
        ...defaultBrandConfig.video.background,
        ...config.video.background,
      },
    },
  };
}

// Load brand configuration from imported modules
export async function loadBrandConfig(brandName?: string): Promise<BrandConfig> {
  try {
    // Import the appropriate brand config
    const { brandConfigs } = await import('./brands');
    
    // Use the specified brand or default to 'default'
    const configKey = (brandName as keyof typeof brandConfigs) || 'default';
    const config = brandConfigs[configKey];
    
    if (!config) {
      throw new Error(`Brand config '${brandName}' not found`);
    }
    
    return validateBrandConfig(config);
  } catch (error) {
    console.warn('Failed to load brand configuration, using hardcoded fallback:', error);
    return defaultBrandConfig;
  }
}
